import {
  HelpDeskAccess,
  HelpDeskProject,
  HelpDeskTicket,
  HelpDeskReply,
  HelpDeskAttachment,
  CreateTicketPayload,
  UpdateTicketPayload,
  CreateReplyPayload,
} from "../Models/helpDesk";
import call from "./call";

export const getHelpDeskAccess = async (): Promise<HelpDeskAccess> => {
  const { data } = await call.get("/v1/help_desk/me/", {
    snack: {
      error: "helpDesk.fetchAccessError",
    },
  });

  return data;
};

export const getHelpDeskProjects = async (): Promise<HelpDeskProject[]> => {
  const { data } = await call.get("/v1/help_desk/projects/", {
    snack: {
      error: "helpDesk.fetchProjectsError",
    },
  });

  return data.projects;
};

export const getMyTickets = async (): Promise<HelpDeskTicket[]> => {
  const { data } = await call.get("/v1/help_desk/tickets/my/", {
    snack: {
      error: "helpDesk.fetchTicketsError",
    },
  });

  return data;
};

export const getManagedTickets = async (): Promise<HelpDeskTicket[]> => {
  const { data } = await call.get("/v1/help_desk/tickets/", {
    snack: {
      error: "helpDesk.fetchTicketsError",
    },
  });

  return data;
};

export const getSingleTicket = async (id: number): Promise<HelpDeskTicket> => {
  const { data } = await call.get(`/v1/help_desk/tickets/${id}/`, {
    snack: {
      error: "helpDesk.fetchTicketError",
    },
  });

  return data;
};

export const createTicket = async (
  payload: CreateTicketPayload,
): Promise<HelpDeskTicket> => {
  const { data } = await call.post("/v1/help_desk/tickets/", payload, {
    snack: {
      success: "helpDesk.createTicketSuccess",
      error: "helpDesk.createTicketError",
    },
  });
  return data;
};

export const updateTicket = async (
  id: number,
  payload: UpdateTicketPayload,
): Promise<HelpDeskTicket> => {
  const { data } = await call.put(`/v1/help_desk/tickets/${id}/`, payload, {
    snack: {
      success: "helpDesk.updateTicketSuccess",
      error: "helpDesk.updateTicketError",
    },
  });

  return data;
};

const attachmentSnack = {
  413: "helpDesk.attachmentTooLarge",
  415: "helpDesk.attachmentType",
};

const filesForm = (files: File[], fields: Record<string, string> = {}) => {
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => form.append(key, value));
  files.forEach((file) => form.append("files", file));
  return form;
};

export const addTicketReply = async (
  id: number,
  payload: CreateReplyPayload,
): Promise<HelpDeskReply> => {
  const body = payload.files?.length
    ? filesForm(payload.files, { message: payload.message })
    : { message: payload.message };
  const { data } = await call.post(
    `/v1/help_desk/tickets/${id}/replies/`,
    body,
    {
      snack: {
        ...attachmentSnack,
        error: "helpDesk.addReplyError",
      },
    },
  );

  return data;
};

export const uploadTicketAttachments = async (
  id: number,
  files: File[],
): Promise<HelpDeskAttachment[]> => {
  const { data } = await call.post(
    `/v1/help_desk/tickets/${id}/attachments/`,
    filesForm(files),
    {
      snack: {
        ...attachmentSnack,
        error: "helpDesk.attachmentUploadError",
      },
    },
  );

  return data.attachments;
};

export const getAttachmentBlob = async (
  attachmentId: number,
): Promise<Blob> => {
  const { data } = await call.get(
    `/v1/help_desk/attachments/${attachmentId}/`,
    {
      responseType: "blob",
      snack: {
        error: "helpDesk.attachmentDownloadError",
      },
    },
  );

  return data;
};

export const deleteTicket = async (id: number): Promise<void> => {
  await call.delete(`/v1/help_desk/tickets/${id}/`, {
    snack: {
      success: "helpDesk.deleteTicketSuccess",
      error: "helpDesk.deleteTicketError",
    },
  });
};
